// components/ApercuInterruptionBDC.tsx
// Contenu de la boîte de dialogue « Aperçu interruption » — partagé par la
// demande (/bondecaisse/anticipation) et la validation
// (/bondecaisse/validation-interruption). Toutes les valeurs proviennent
// du serveur (fn_BDC_CalculInterruption) : paramètres du type (ParamBDC,
// onglet « Paramètre interruption »), intérêts, taxes dues / récupérées,
// pénalité, frais et impact net client.
import React from "react";
import { Alert, Table } from "antd";
import formatNumber from "../../../Functions/Traitement/FormatNumber";

export interface IApercuInterruption {
  [key: string]: any;
  BLOQUANTS?: string[];
  ALERTES?: string[];
}

const n = (v: any) => Number(v) || 0;
const fmt = (v: any) => formatNumber(n(v));
const fmtDate = (d: any) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('fr-FR');
};

export const libModeInter = (mode?: string) => {
  switch ((mode || 'NON').toUpperCase()) {
    case 'ACQ': return 'Le client garde les intérêts des jours écoulés';
    default: return 'La banque reprend tous les intérêts';
  }
}

const sens = (v: any, pos: string, neg: string) => n(v) === 0 ? '—' : `${fmt(Math.abs(n(v)))} (${n(v) > 0 ? pos : neg})`;

function Section({ titre, cells }: { titre: string, cells: any[][] }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: '#116599', fontWeight: 600, borderBottom: '1px solid #e8e8e8', marginBottom: 6 }}>{titre}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px 16px', fontSize: 13 }}>
        {cells.map((cell, i) => (
          <div key={i}><b>{cell[0]} :</b> {cell[1]}</div>
        ))}
      </div>
    </div>
  );
}

export default function ApercuInterruptionBDC({ apercu, entete }: { apercu: IApercuInterruption, entete?: any[][] }) {
  const a = apercu;

  // Récapitulatif des taxes appliquées (dues / récupérées)
  const taxes = [
    {
      key: 'int', libelle: 'Taxe sur intérêts servis (TXTAXE)', base: fmt(a.INTBRUTSERVI), taux: `${n(a.TXTAXE)} %`,
      montant: fmt(a.TAXESERVIE), effet: n(a.TAXESERVIE) > 0 ? 'Retenue sur intérêts — due au Trésor' : 'Aucune (intérêts non servis)',
    },
    {
      key: 'adj', libelle: 'Régularisation taxe déjà comptabilisée', base: fmt(a.TAXECOMPTABILISEE), taux: '—',
      montant: fmt(Math.abs(n(a.AJUSTTAXE))),
      effet: n(a.AJUSTTAXE) > 0 ? 'Complément dû (D taxe débit / C taxe crédit)' : n(a.AJUSTTAXE) < 0 ? 'Récupérée — extourne (D taxe crédit / C taxe débit)' : 'Aucune',
    },
  ];

  // Taux appliqués : taux paramétré + base + montant calculé
  // (mêmes formules que fn_BDC_CalculInterruption, année de 360 jours)
  const taux = [
    {
      key: 'txint', libelle: 'Taux d\'intérêt (TXINT)', taux: `${n(a.TXINT)} %/an`, base: fmt(a.MONTANT),
      duree: `${n(a.JOURECOULE)} j écoulés`, formule: 'Montant × taux × jours écoulés / 36 000 (plafonné aux intérêts contractuels)',
      montant: fmt(a.INTACQUISBRUT),
    },
    {
      key: 'txtaxe', libelle: 'Taux taxe intérêts (TXTAXE)', taux: `${n(a.TXTAXE)} %`, base: fmt(a.INTACQUISBRUT),
      duree: '—', formule: 'Intérêts acquis bruts × taux / 100',
      montant: fmt(a.TAXINTACQ),
    },
    {
      key: 'taxeval', libelle: 'Taxe déjà retenue à la validation (retenue à la source)', taux: a.TYPIMPUTINT === 'POST' ? 'dotations SOD' : `${n(a.TXTAXE)} %`,
      base: fmt(a.TAXECOMPTABILISEE), duree: '—',
      formule: n(a.AJUSTTAXE) < 0 ? 'Intérêts non servis → extourne (D taxe crédit / C taxe débit)' : n(a.AJUSTTAXE) > 0 ? 'Complément dû (D taxe débit / C taxe crédit)' : 'Conservée (intérêts servis couvrent la taxe)',
      montant: fmt(Math.abs(n(a.AJUSTTAXE))),
    },
    {
      key: 'pen', libelle: `Pénalité interruption (grille ${a.CODOPEFRAIS || ''})`, taux: 'grille', base: fmt(n(a.MONTANT) * n(a.JOURRESTANT) / 360),
      duree: `${n(a.JOURRESTANT)} j restants`, formule: 'Montant × jours restants / 360 → palier tarifaire du code opération',
      montant: fmt(a.PENALITE),
    },
  ];

  return (
    <div>
      {(a.BLOQUANTS || []).length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 10 }} message="Opération impossible"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{(a.BLOQUANTS || []).map((b, i) => <li key={i}>{b}</li>)}</ul>} />
      )}
      {(a.ALERTES || []).length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 10 }} message="Avertissements"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{(a.ALERTES || []).map((b, i) => <li key={i}>{b}</li>)}</ul>} />
      )}

      {entete && entete.length > 0 && <Section titre="Demande" cells={entete} />}

      <Section titre="Bon de caisse" cells={[
        ['Bon de caisse', a.NUMBDC], ['Client', `${a.NOMCLI || ''} (${a.NUMCLI || ''})`],
        ['Type', a.TYPBDC], ['Imputation', a.TYPIMPUTINT === 'POST' ? 'POST — postcompté' : 'PRE — précompté'],
        ['Montant', fmt(a.MONTANT)], ['Taux intérêt', `${n(a.TXINT)} %`],
        ['Date début', fmtDate(a.DATDEBUT)], ['Date fin', fmtDate(a.DATFIN)],
        ['Journée comptable', fmtDate(a.DATJOUR)], ['Date d\'interruption', fmtDate(a.DATINTERUP)],
        ['Jours écoulés', n(a.JOURECOULE)], ['Jours restants', n(a.JOURRESTANT)],
      ]} />

      <Section titre="Paramètres appliqués (ParamBDC — Paramètre interruption)" cells={[
        ['Interruption autorisée', a.INTERRUPTION === 'O' ? 'Oui' : 'Non'], ['Durée min. (jours)', n(a.DUREEMININTERUPT)],
        ['Mode intérêts', `${a.MODEINT} — ${libModeInter(a.MODEINT)}`], ['Code op. pénalité', a.CODOPEFRAIS || 'Aucun'],
      ]} />

      <div style={{ marginBottom: 12 }}>
        <div style={{ color: '#116599', fontWeight: 600, borderBottom: '1px solid #e8e8e8', marginBottom: 6 }}>Taux appliqués</div>
        <Table size="small" pagination={false} rowKey="key" dataSource={taux} columns={[
          { title: 'Taux', dataIndex: 'libelle', key: 'libelle' },
          { title: '%', dataIndex: 'taux', key: 'taux', align: 'right', width: 90 },
          { title: 'Base', dataIndex: 'base', key: 'base', align: 'right', width: 120 },
          { title: 'Durée', dataIndex: 'duree', key: 'duree', align: 'right', width: 110 },
          { title: 'Calcul', dataIndex: 'formule', key: 'formule' },
          { title: 'Montant calculé', dataIndex: 'montant', key: 'montant', align: 'right', width: 130, render: (v: string) => <b>{v}</b> },
        ]} />
      </div>

      <Section titre="Intérêts" cells={[
        ['Intérêts contractuels bruts', fmt(a.MTNINT)], ['Taxe contractuelle', fmt(a.MTNTAXE)],
        ['Net contractuel', fmt(a.MTNNETAPAYER)], ['Intérêts provisionnés', fmt(a.INTPROVISIONNE)],
        ['Intérêts acquis bruts', fmt(a.INTACQUISBRUT)], ['Taxe sur intérêts acquis', fmt(a.TAXINTACQ)],
        ['Intérêts acquis nets', fmt(a.INTACQUIS)], ['Intérêts non acquis (dus)', fmt(a.INTDUS)],
        ['Intérêts bruts servis', fmt(a.INTBRUTSERVI)], ['Intérêts nets conservés', fmt(a.INTCONSERVE)],
        ['Intérêts déjà versés', fmt(a.INTDEJAVERSE)],
        ['Compte intérêts client', sens(a.AJUSTCLIENT, 'versement', 'reprise')],
      ]} />

      <div style={{ marginBottom: 12 }}>
        <div style={{ color: '#116599', fontWeight: 600, borderBottom: '1px solid #e8e8e8', marginBottom: 6 }}>Taxes appliquées</div>
        <Table size="small" pagination={false} rowKey="key" dataSource={taxes} columns={[
          { title: 'Taxe', dataIndex: 'libelle', key: 'libelle' },
          { title: 'Base', dataIndex: 'base', key: 'base', align: 'right', width: 130 },
          { title: 'Taux', dataIndex: 'taux', key: 'taux', align: 'right', width: 80 },
          { title: 'Montant', dataIndex: 'montant', key: 'montant', align: 'right', width: 130 },
          { title: 'Effet', dataIndex: 'effet', key: 'effet' },
        ]} />
      </div>

      <Section titre="Pénalité d'interruption (grille tarifaire)" cells={[
        ['Base de calcul', fmt(n(a.MONTANT) * n(a.JOURRESTANT) / 360)], ['Pénalité prélevée', fmt(a.PENALITE)],
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#e8f4fb', border: '1px solid #116599', borderRadius: 4, fontSize: 14 }}>
        <span>
          Capital {fmt(a.MONTANT)} + intérêts nets conservés {fmt(a.INTCONSERVE)} − pénalité {fmt(a.PENALITE)}
          {n(a.AJUSTTAXE) !== 0 && ` — taxe ${n(a.AJUSTTAXE) < 0 ? 'récupérée' : 'complément'} ${fmt(Math.abs(n(a.AJUSTTAXE)))} (comptes banque, sans impact client)`}
        </span>
        <b>Impact net client : {fmt(a.NETCLIENT)}</b>
      </div>
    </div>
  );
}
